// H2.3 WO#3c — Point-wise canonical zone classification (SHELL LEVEL).
//
// This hook consumes the ONLY canonical zone truth source available
// today: `TheGridSaleV2.quote()` via `GET /api/v1/sale/quote?x&y&w&h`
// (routed through the ratified `saleApi.quote()` adapter).
//
// Devil-review boundary (WO#3a frozen baseline + WO#3c validation round):
//   * The viewer (`GridViewer`) is UNTOUCHED. Zone data is a
//     SHELL/product concern (Neo verdict WO#3c).
//   * `ViewerContextState` variants remain the frozen WO#3a triad
//     (empty | selection | parcel).
//   * NO persistent module-level cache — the earlier geometry-only
//     cache was flagged by Neo as a silent-skew vector: it would
//     survive a canonical authority change (e.g. a future SaleV3
//     activation) and continue to serve classifications from the
//     stale authority. See WO#3c validation §3. We therefore rely on
//     an in-flight race guard only — no cross-mount cache persists.
//   * No polling. No fetch on mouse-move. Uses `saleApi.quote()` —
//     the SAME adapter every other quote consumer uses; zero new API.

import { useEffect, useRef, useState } from "react";
import { saleApi } from "../lib/sale-api";
import type { GridSelection } from "../lib/grid-selection";
import type { SaleQuote, ZoneBreakdown } from "../lib/sale-types";

export type ZoneClassificationStatus =
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "available";
      /**
       * Canonical `zone` string emitted by the backend. Callers must
       * NOT translate this into arbitrary marketing labels — render
       * verbatim.
       */
      zone: string;
      /** Canonical `quote_result` (V2). Not present for V1 quotes. */
      quoteResult: string | null;
      /** Whether the classifier considers the rectangle sellable. */
      sellable: boolean;
      /** Full canonical zone breakdown, populated for every V2 outcome. */
      breakdown: ZoneBreakdown | null;
      /** Advisory / block-pinned identity of the classification. */
      quoteAsOfBlock: number | null;
    }
  | { status: "unavailable"; reason: string };

function _key(sel: GridSelection): string {
  return `${sel.x},${sel.y},${sel.w},${sel.h}`;
}

/**
 * Pure adapter from a `SaleQuote` payload to the shell-facing
 * `ZoneClassificationStatus`. Exported for unit tests.
 */
export function fromQuote(q: SaleQuote): ZoneClassificationStatus {
  if (q.generation === "v2") {
    return {
      status: "available",
      zone: q.zone,
      quoteResult: q.quote_result,
      sellable: q.sellable,
      breakdown: q.zone_breakdown,
      quoteAsOfBlock: q.quote_as_of_block,
    };
  }
  // V1 legacy path — no zone information canonically available.
  return { status: "unavailable", reason: "legacy_quote_generation" };
}

/**
 * Pure adapter from a thrown error to the shell-facing
 * `ZoneClassificationStatus`. Exported for unit tests.
 */
export function fromError(err: unknown): ZoneClassificationStatus {
  const reason = err instanceof Error ? err.message : String(err);
  return { status: "unavailable", reason };
}

/**
 * Fetch zone classification for a rectangle. `input === null` short-
 * circuits to `{ status: "idle" }` with zero network activity.
 *
 * Race guard: the latest requested key is tracked in a ref; slow
 * responses for stale keys are dropped rather than applied.
 *
 * No persistent cache — see the module header for why (authority skew).
 */
export function useZoneClassification(
  input: GridSelection | null,
): ZoneClassificationStatus {
  const [state, setState] = useState<ZoneClassificationStatus>(() =>
    input ? { status: "loading" } : { status: "idle" },
  );
  const latestKeyRef = useRef<string | null>(input ? _key(input) : null);
  const key = input ? _key(input) : null;

  useEffect(() => {
    if (!input) {
      latestKeyRef.current = null;
      setState({ status: "idle" });
      return;
    }
    const myKey = _key(input);
    latestKeyRef.current = myKey;
    setState({ status: "loading" });
    let cancelled = false;
    void (async () => {
      try {
        const q = await saleApi.quote(input);
        if (cancelled) return;
        if (latestKeyRef.current !== myKey) return;
        setState(fromQuote(q));
      } catch (err) {
        if (cancelled) return;
        if (latestKeyRef.current !== myKey) return;
        setState(fromError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
    // Depend on the string key so that a fresh geometry triggers a
    // new fetch, but an unrelated re-render does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
